import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex items-center text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "space-y-2" }
const _hoisted_4 = { class: "flex items-center space-x-4" }
const _hoisted_5 = {
  key: 1,
  class: "space-y-2"
}

import { CalendarIcon } from '@heroicons/vue/solid';
import { toast, toastServiceError } from '@/services';
import WorkScheduleService from '@/services/api/WorkScheduleService';
import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import { WorkScheduleModel } from '@/types/models/WorkScheduleModel';
import { WorkScheduleGetAllParams } from '@/types/services/WorkScheduleService';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
    FilterItem,
    LevelFilter,
    Permission,
    SortMenuItem,
    WorkScheduleActions,
    WorkScheduleGetters,
} from '@/types';
import {
    WorkScheduleListFilters,
    WorkScheduleListItem,
    WorkScheduleListSort,
} from '@/views/pages/work-schedule/components';

const perPage = 10;

// Computed

export default /*@__PURE__*/_defineComponent({
  __name: 'WorkSchedulePage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const uiStore = useUiStore();
const filterStore = useFilterStore();

const workSchedules = ref<WorkScheduleModel[]>([]);
const count = ref(0);
const search = computed({
    get: () => store.getters[WorkScheduleGetters.SEARCH],
    set: (value: string) => store.dispatch(WorkScheduleActions.SET_SEARCH, value),
});
const sortMenuSelected = computed<SortMenuItem[]>({
    get: () => store.getters[WorkScheduleGetters.SORT],
    set: (value: SortMenuItem[]) => store.dispatch(WorkScheduleActions.SET_SORT, value),
});
const currentPage = computed({
    get: () => store.getters[WorkScheduleGetters.PAGE],
    set: (value: number) => store.dispatch(WorkScheduleActions.SET_PAGE, value),
});

const filter: LevelFilter = store.getters[WorkScheduleGetters.FILTER];
const activeFilters = computed<FilterItem[]>(
    () => store.getters[WorkScheduleGetters.ACTIVE_FILTERS],
);
const levelFilter = computed(() => filterStore.level);
const orderBy = computed(() => sortMenuSelected.value.map((s) => s.direction + s.key).join(','));

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getQueryParams = (): WorkScheduleGetAllParams => ({
    page: currentPage.value,
    per_page: perPage,
    order_by: orderBy.value,
    is_active: filter.isActive !== null ? (filter.isActive.value as boolean) : undefined,
    search: search.value,
});

const fetchAllWorkSchedules = async () => {
    try {
        setLoading(true);
        const { data, headers } = await WorkScheduleService.getAll<WorkScheduleModel[]>({
            params: getQueryParams(),
        });
        workSchedules.value = data;
        count.value = parseInt(headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const updateWorkScheduleStatus = async (workSchedule: WorkScheduleModel, isActive: boolean) => {
    try {
        setLoading(true);
        const { data } = await (isActive
            ? WorkScheduleService.activate(workSchedule.id)
            : WorkScheduleService.inactivate(workSchedule.id));
        toast.success(data.message);
        await fetchAllWorkSchedules();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleActivateClick = (item: WorkScheduleModel) => updateWorkScheduleStatus(item, true);
const handleInactivateClick = (item: WorkScheduleModel) => updateWorkScheduleStatus(item, false);

const handleRemoveSortClick = (item: SortMenuItem) => {
    const index = sortMenuSelected.value.indexOf(item);
    if (index > -1) sortMenuSelected.value.splice(index, 1);
};
const handleRemoveFilterClick = (item: FilterItem) =>
    store.dispatch(WorkScheduleActions.CLEAR_FILTER, item);

// Watchers
watch([currentPage], fetchAllWorkSchedules);
watch([levelFilter, search, filter, orderBy], () => {
    if (currentPage.value === 1) fetchAllWorkSchedules();
    else currentPage.value = 1;
});

onMounted(() => {
    if (route.query.clearStateOnSetup) {
        search.value = '';
        sortMenuSelected.value = [];
        currentPage.value = 1;
        router.replace({ query: {} });
    }

    fetchAllWorkSchedules();
});

return (_ctx: any,_cache: any) => {
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiListSearch = _resolveComponent("UiListSearch")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiSortMenuBadge = _resolveComponent("UiSortMenuBadge")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, [
          _createVNode(_unref(CalendarIcon), {
            class: "mr-1 h-6 w-6 text-gray-500",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('core.entity.WorkSchedules')), 1)
        ])
      ]),
      actions: _withCtx(() => [
        (_ctx.$can(_unref(Permission).CREATE_WORK_SCHEDULE))
          ? (_openBlock(), _createBlock(_component_router_link, {
              key: 0,
              to: { name: 'CreateWorkSchedule' }
            }, {
              default: _withCtx(() => [
                _createVNode(_component_UiButton, {
                  variant: "primary",
                  "text-variant": "white"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Add')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_component_UiPanel, { class: "flex px-4 py-2" }, {
        default: _withCtx(() => [
          _createVNode(_component_UiListSearch, {
            placeholder: _ctx.$t('workSchedule.filters.search'),
            class: "flex-1"
          }, null, 8, ["placeholder"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(WorkScheduleListSort), {
              modelValue: sortMenuSelected.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((sortMenuSelected).value = $event))
            }, null, 8, ["modelValue"]),
            _createVNode(_unref(WorkScheduleListFilters))
          ])
        ]),
        _: 1
      }),
      (sortMenuSelected.value.length || activeFilters.value.length)
        ? (_openBlock(), _createBlock(_component_UiPanel, {
            key: 0,
            class: "space-x-2"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortMenuSelected.value, (selected) => {
                return (_openBlock(), _createBlock(_component_UiSortMenuBadge, {
                  key: selected.key,
                  item: selected,
                  onRemove: ($event: any) => (handleRemoveSortClick(selected))
                }, null, 8, ["item", "onRemove"]))
              }), 128)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (item) => {
                return (_openBlock(), _createBlock(_component_UiBadge, {
                  key: `activeFilters${item.field}`,
                  removable: "",
                  onRemove: ($event: any) => (handleRemoveFilterClick(item))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t(`workSchedule.filters.${item.field}.label`)) + ": " + _toDisplayString(item.label), 1)
                  ]),
                  _: 2
                }, 1032, ["onRemove"]))
              }), 128))
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (workSchedules.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(workSchedules.value, (workSchedule) => {
              return (_openBlock(), _createBlock(_unref(WorkScheduleListItem), {
                key: workSchedule.id,
                "model-value": workSchedule,
                onActivate: handleActivateClick,
                onInactivate: handleInactivateClick
              }, null, 8, ["model-value"]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (workSchedules.value.length)
        ? (_openBlock(), _createBlock(_component_UiPagination, {
            key: 2,
            modelValue: currentPage.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((currentPage).value = $event)),
            count: count.value,
            perPage: perPage
          }, null, 8, ["modelValue", "count"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})